import type { Lesson } from './types'

/**
 * Qalqalah - the "echoing" or "bouncing" sound given to five specific
 * letters whenever they carry a sukun, whether that sukun is original or
 * only appears because recitation stopped on that letter.
 */
export const qalqalahLesson: Lesson = {
  id: 'qalqalah',
  title: 'Qalqalah (Echoing Sound)',
  summary: 'Five letters get a small bounce/echo whenever they carry sukun: ق ط ب ج د.',
  explanation: [
    'Qalqalah (قلقلة, "vibration" or "echo") applies to five letters, remembered by the phrase قُطْبُ جَدٍّ (quṭbu jadd): ق ط ب ج د.',
    'Whenever one of these letters carries a sukun - either because it naturally has no vowel, or because recitation stops on it at the end of a word - the sound is given a slight bounce or echo instead of being cut off flatly. This happens because all five letters are pronounced by fully closing off airflow (at the throat, tongue, or lips), and releasing that closure produces a small natural echo.',
    'There are two strengths:',
    'Minor qalqalah (قلقلة صغرى) - the letter has sukun in the middle of recitation (not at a stop). The bounce is light.',
    'Major qalqalah (قلقلة كبرى) - recitation stops on the letter, whether it originally had a vowel or sukun. The bounce is more pronounced, since the full stop gives it more emphasis.',
  ],
  colorLegend: [{ color: 'qalqalah', label: 'Qalqalah letter (bounced/echoed)' }],
  examples: [
    { arabic: 'يَجْعَلُونَ', transliteration: 'yajʿalūn', meaning: 'they make', highlights: [{ text: 'جْ', color: 'qalqalah' }] },
    { arabic: 'يَقْطَعُونَ', transliteration: 'yaqṭaʿūn', meaning: 'they cut off', highlights: [{ text: 'قْ', color: 'qalqalah' }] },
    { arabic: 'أَحَدْ', transliteration: 'aḥad', meaning: 'One (stopping here = major qalqalah)', highlights: [{ text: 'دْ', color: 'qalqalah' }] },
    { arabic: 'الْفَلَقْ', transliteration: 'al-falaq', meaning: 'the daybreak (stopping here = major qalqalah)', highlights: [{ text: 'قْ', color: 'qalqalah' }] },
    { arabic: 'يَقْبَلُ', transliteration: 'yaqbalu', meaning: 'he accepts (minor qalqalah, mid-word)', highlights: [{ text: 'قْ', color: 'qalqalah' }] },
  ],
}
